/**
 * Base-folder discovery and target resolution inside the `bases/` subtree — the
 * counterpart of `workspace.ts`, which does the same job for `hubs/`.
 *
 * It lives in `lib/` beside its hub sibling, not under `data/`, because both the
 * subtree ROUTER (`subtree-routing.ts`) and the base config-as-code commands
 * need it: the router's rule-5 candidate set spans both subtrees, and a router
 * that re-implemented the base scan would drift from the resolver it is routing
 * to — the two would then disagree about what a candidate is, and the refusal
 * would name a target the resolver never sees.
 *
 * The dir itself comes from the ONE layout resolver (`resolveBasesDir`), so a
 * future layout change moves both subtrees together
 * (docs/reference/rekor-platform-contracts.md §3.3).
 *
 * Resolution here is *within* the subtree: which base folder, given that the
 * router has already decided this invocation is a base one. Ambiguity at this
 * level keeps its own "say which one" message — it is not the mixed-invocation
 * refusal.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { resolveBasesDir, basesDirLabel, isDirectory } from './layout.js';
import { expected } from './expected.js';
import { PATH_SAFE_ID_RULE, isPathSafeId } from './base-id.js';
import { readFileNoFollow } from './fs-safety.js';

/**
 * The per-folder identity file and the field naming its base. Defined here — with
 * the readers and the writer that produce them — so a rename breaks every reader
 * at compile time.
 */
export const BASE_META_FILE = 'base.yaml';

/** The subset of `base.yaml` this layer reads. The writer owns the full shape. */
export interface BaseMetaShape {
  base_id?: string;
  environment?: string;
}

export interface BaseFolder<M extends BaseMetaShape = BaseMetaShape> {
  folder: string;
  meta: M;
}

/**
 * Read a folder's `base.yaml`. Null when absent, unreadable, or not a YAML
 * mapping — the same "a malformed identity file is not an identity" rule
 * `readHubYaml` applies on the hub side, so a half-written file is never
 * counted as a folder either subtree would resolve to.
 */
export function readBaseMeta<M extends BaseMetaShape = BaseMetaShape>(folder: string): M | null {
  // No-follow: a symlinked `base.yaml` would let a file from outside the repo
  // supply the base's identity, and its `name`/`description` reach the server on
  // push's create branch.
  const bytes = readFileNoFollow(folder, path.join(folder, BASE_META_FILE));
  if (bytes === null) return null;
  let doc: unknown;
  try {
    doc = yaml.load(bytes.toString('utf-8'));
  } catch {
    return null;
  }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return null;
  return doc as M;
}

/** Every `bases/<x>/` folder carrying a readable `base.yaml`. */
export function listBaseFolders(basesDir: string): BaseFolder[] {
  let entries: string[];
  try {
    // Dot-prefixed entries are skipped, matching `safeReaddir` on the hub side:
    // `bases/.trash/base.yaml` is not a folder anything resolves to.
    entries = fs.readdirSync(basesDir).filter((entry) => !entry.startsWith('.'));
  } catch {
    return [];
  }
  const out: BaseFolder[] = [];
  for (const name of entries.sort()) {
    const folder = path.join(basesDir, name);
    if (!isDirectory(folder)) continue;
    const meta = readBaseMeta(folder);
    if (meta) out.push({ folder, meta });
  }
  return out;
}

/**
 * True when a folder's `base.yaml` marks it a read-only production mirror —
 * written alongside a preview by `pull` so the live production config is
 * browsable, never an edit target. Excluded from auto-resolution, refused by
 * `push` (production is `wayai bases promote`).
 */
export function isProductionFolder(meta: BaseMetaShape): boolean {
  return meta.environment === 'production';
}

/** A base folder is an editable target only when it is not a production mirror. */
export function filterEditableBases(bases: BaseFolder[]): BaseFolder[] {
  return bases.filter((b) => !isProductionFolder(b.meta));
}

/** True when `child` is `parent` or sits beneath it. Lexical — see `fs-safety.ts` for no-follow. */
export function isUnder(parent: string, child: string): boolean {
  const rel = path.relative(parent, child);
  return rel === '' || (!rel.startsWith('..') && !path.isAbsolute(rel));
}

/** The base folder enclosing `cwd`, so a bare `pull`/`push` run inside one targets it. */
export function findEnclosingBaseFolder(basesDir: string, cwd: string): BaseFolder | null {
  let dir = path.resolve(cwd);
  const stop = path.resolve(basesDir);
  while (isUnder(stop, dir)) {
    const meta = readBaseMeta(dir);
    if (meta) return { folder: dir, meta };
    const parent = path.dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

export interface ResolvedBaseTarget {
  /** Absolute path to the base folder. May not exist yet — a first pull creates it. */
  folder: string;
  /**
   * Whether the folder already carries a `base.yaml` AT ALL.
   *
   * Deliberately separate from `meta`, because a file that is PRESENT but
   * malformed is not the same as an absent one and the two answer different
   * questions. This one answers "is there local work here?" — it gates the
   * overwrite prompt and the changed-file listing. Folding the two together
   * makes a YAML typo in `base.yaml` read as a first pull, so the prompt and
   * the listing are both skipped and the user's edits go without a word.
   */
  exists: boolean;
  /**
   * The folder's parsed `base.yaml`, or null when absent OR unparseable. Answers
   * "what does it say?" — it supplies the base id. Carried from the resolution
   * that already read it so callers don't re-read (and can't disagree with) the
   * same file.
   */
  meta: BaseMetaShape | null;
}

/**
 * Resolve a selector to an absolute base-folder path INSIDE `basesDir`.
 *
 * Every selector is validated as a base id before it is joined — it becomes a
 * filesystem path here, so a traversal segment (`..`), a separator, or an
 * absolute path is rejected ahead of the join rather than after.
 *
 * There is deliberately no path form. The subtree router normalizes every
 * legitimate spelling — `bases/crm`, `wayai-ws/bases/crm`, a relative or
 * absolute path under the bases dir — down to the bare name before calling
 * here, so the only selectors that arrive with a separator are the two RAW
 * sources: the `--base` flag (documented `<id|name>`, not a path) and
 * `wayai-ws/wayai.yaml`'s `default_base`. Accepting a path from either resolved
 * the target OUTSIDE the workspace — and, because `writeBaseFolder` roots its
 * no-symlink guard at the target's own parent, took the containment with it.
 */
export function folderForSelector(basesDir: string, selector: string): string {
  assertValidBaseSelector(selector);
  return path.join(basesDir, selector);
}

/**
 * Refuse a selector that cannot be a single folder name, naming it.
 *
 * Checked against `isPathSafeId` rather than the API's base-id rule: the duty
 * here is containment of a filesystem join, and a selector also names a FOLDER,
 * which may be a base whose stored id predates that rule. Refusing those would
 * make a base the API still serves unreachable from the workspace.
 */
export function assertValidBaseSelector(selector: string, source = 'base'): void {
  if (!isPathSafeId(selector)) {
    throw expected(
      `Invalid ${source} "${selector}". A base selector is one folder name — ${PATH_SAFE_ID_RULE}.`,
    );
  }
}

/**
 * Which base folder this invocation targets. The subtree router has already
 * applied §3.3's cross-subtree precedence and passes the selector it resolved;
 * what is left is the within-subtree fallback — the enclosing folder, then the
 * sole editable preview.
 *
 * Throws an actionable, expected error when nothing or more than one thing
 * matches. That ambiguity is this side's own, and keeps this side's wording.
 */
export function resolveBaseTarget(
  gitRoot: string,
  selector: string | undefined,
  cwd: string = process.cwd(),
): ResolvedBaseTarget {
  const basesDir = resolveBasesDir(gitRoot);

  if (selector) {
    const folder = folderForSelector(basesDir, selector);
    // Belt and braces on the invariant the whole layer rests on: every write
    // below roots its containment at this folder, so if this folder itself were
    // ever outside the bases dir, none of that containment would mean anything.
    if (!isUnder(basesDir, folder)) {
      throw expected(`Refusing to target ${folder}: it is outside ${basesDir}.`);
    }
    return { folder, exists: hasBaseMetaFile(folder), meta: readBaseMeta(folder) };
  }

  const enclosing = findEnclosingBaseFolder(basesDir, cwd);
  if (enclosing) return { folder: enclosing.folder, exists: true, meta: enclosing.meta };

  const editable = filterEditableBases(listBaseFolders(basesDir));
  if (editable.length === 1) {
    return { folder: editable[0].folder, exists: true, meta: editable[0].meta };
  }

  const label = basesDirLabel(gitRoot);
  if (editable.length === 0) {
    throw expected(
      `No base folders found in ${label}/. Pass a base id to fetch one for the first time (e.g. \`wayai pull bases/<base>\`).`,
    );
  }
  throw expected(
    [
      `Multiple bases found in ${label}/. Pass --base <id|folder-name> to choose, or run from inside a base folder:`,
      ...editable.map((b) => `  ${path.basename(b.folder)} (${b.meta.base_id ?? 'not created yet'})`),
    ].join('\n'),
  );
}

/**
 * The base id a target resolves to: the folder's own `base_id` when it has one,
 * otherwise the selector (a first pull of a not-yet-tracked base). Null when
 * neither is known.
 */
export function targetBaseId(
  target: ResolvedBaseTarget,
  selector: string | undefined,
): string | null {
  if (target.meta?.base_id) return target.meta.base_id;
  if (selector && !selector.includes('/') && !selector.includes(path.sep)) return selector;
  return target.exists ? null : path.basename(target.folder);
}

/** Whether the folder carries a `base.yaml` at all — present-but-malformed included. */
export function hasBaseMetaFile(folder: string): boolean {
  try {
    return fs.lstatSync(path.join(folder, BASE_META_FILE)).isFile();
  } catch {
    return false;
  }
}
